// Rules for the 501 game: a match of sets, each a race to win legs, each leg
// a turn-based countdown from the chosen starting score to exactly 0. Each
// turn is entered as a single 3-dart total (this is the "numpad" variant -
// it doesn't know which individual darts made up that total, so unlike a
// dartboard-tap variant it can't enforce finishing on a double; reaching
// exactly 0 wins the leg regardless of how). Going below 0, or to exactly 1
// (no possible double leaves a checkout from there), is a bust - the turn is
// discarded and the player's remaining score is unchanged.
//
// The starting score (301/501/701/1001) is a per-match setup choice, not a
// separate game - see configRows below and app.js's setup screen. The
// checkout chart itself doesn't depend on it (a remaining-121 checkout is
// the same whichever total you started from).
//
// "Best of N" (both for sets and for legs-per-set) means first to a majority
// of N wins - best of 3 is first to 2, best of 5 is first to 3, etc.

export const DEFAULT_BEST_OF_SETS = 3;
export const DEFAULT_BEST_OF_LEGS = 3;
export const DEFAULT_STARTING_SCORE = 501;
export const STARTING_SCORE_OPTIONS = [301, 501, 701, 1001];
export const BEST_OF_OPTIONS = [1, 3, 5, 7];

export function isBust(remainingBefore, turnScore) {
    const after = remainingBefore - turnScore;
    return after < 0 || after === 1;
}

// The 9 "bogey" totals above 100 that no combination of 3 darts can ever add
// up to (every other integer from 0 to 180 is reachable). Anything above 180
// is impossible too - it's more than three triple-20s.
const IMPOSSIBLE_THREE_DART_SCORES = new Set([163, 166, 169, 172, 173, 175, 176, 178, 179]);

export function isValidTurnScore(score) {
    return Number.isInteger(score) && score >= 0 && score <= 180 && !IMPOSSIBLE_THREE_DART_SCORES.has(score);
}

export function toWin(bestOf) {
    return Math.floor(bestOf / 2) + 1;
}

// The full standard 3-dart checkout chart (every finishable total from 170
// down to 2 - the ones missing, like 169 or 159, have no possible checkout
// and are correctly absent). "S"-prefixed tokens are a plain single; a few
// totals have more than one accepted route, separated by " / ".
const CHECKOUTS = {
    170: 'T20 T20 Bull', 167: 'T20 T19 Bull', 164: 'T20 T18 Bull', 161: 'T20 T17 Bull',
    160: 'T20 T20 D20', 158: 'T20 T20 D19', 157: 'T20 T19 D20', 156: 'T20 T20 D18',
    155: 'T20 T19 D19', 154: 'T20 T18 D20', 153: 'T20 T19 D18', 152: 'T20 T20 D16',
    151: 'T20 T17 D20', 150: 'T20 T18 D18', 149: 'T20 T19 D16', 148: 'T20 T16 D20',
    147: 'T20 T17 D18', 146: 'T20 T18 D16', 145: 'T20 T15 D20', 144: 'T20 T20 D12',
    143: 'T20 T17 D16', 142: 'T20 T14 D20', 141: 'T20 T19 D12', 140: 'T20 T16 D16',
    139: 'T20 T13 D20', 138: 'T20 T18 D12', 137: 'T20 T15 D16', 136: 'T20 T20 D8',
    135: 'T20 T17 D12', 134: 'T20 T14 D16', 133: 'T20 T19 D8', 132: 'T20 T16 D12',
    131: 'T20 T13 D16', 130: 'T20 T18 D8', 129: 'T19 T16 D12', 128: 'T18 T14 D16',
    127: 'T20 T17 D8', 126: 'T19 T19 D6', 125: 'T20 T15 D10', 124: 'T20 T16 D8',
    123: 'T19 T16 D9', 122: 'T18 T18 D7', 121: 'T20 T11 D14', 120: 'T20 S20 D20',
    119: 'T19 T12 D13', 118: 'T20 S18 D20', 117: 'T20 S17 D20', 116: 'T20 S16 D20',
    115: 'T20 S15 D20', 114: 'T20 S14 D20', 113: 'T20 S13 D20', 112: 'T20 S12 D20',
    111: 'T20 S11 D20', 110: 'T20 S10 D20 / T19 S13 D20', 109: 'T20 S9 D20', 108: 'T20 S8 D20',
    107: 'T20 S7 D20', 106: 'T20 S6 D20', 105: 'T20 S5 D20', 104: 'T20 S4 D20',
    103: 'T20 S3 D20', 102: 'T20 S2 D20', 101: 'T20 S1 D20', 100: 'T20 D20',
    99: 'T19 S10 D16', 98: 'T20 D19', 97: 'T19 D20', 96: 'T20 D18',
    95: 'T19 D19', 94: 'T18 D20', 93: 'T19 D18', 92: 'T20 D16',
    91: 'T17 D20', 90: 'T18 D18', 89: 'T19 D16', 88: 'T20 D14',
    87: 'T17 D18', 86: 'T18 D16', 85: 'T19 D14', 84: 'T20 D12',
    83: 'T17 D16', 82: 'T14 D20', 81: 'T19 D12', 80: 'T20 D10',
    79: 'T13 D20', 78: 'T18 D12', 77: 'T19 D10', 76: 'T20 D8',
    75: 'T17 D12', 74: 'T14 D16', 73: 'T19 D8', 72: 'T16 D12',
    71: 'T13 D16', 70: 'T18 D8', 69: 'T19 D6', 68: 'T20 D4',
    67: 'T17 D8', 66: 'T10 D18', 65: 'T19 D4', 64: 'T16 D8',
    63: 'T13 D12', 62: 'T10 D16', 61: 'T15 D8', 60: 'S20 D20',
    59: 'S19 D20', 58: 'S18 D20', 57: 'S17 D20', 56: 'S16 D20',
    55: 'S15 D20', 54: 'S14 D20', 53: 'S13 D20', 52: 'S12 D20',
    51: 'S11 D20', 50: 'S18 D16', 49: 'S9 D20', 48: 'S16 D16',
    47: 'S15 D16', 46: 'S10 D18', 45: 'S13 D16', 44: 'S4 D20',
    43: 'S3 D20', 42: 'S10 D16', 41: 'S9 D16', 40: 'D20',
    39: 'S7 D16', 38: 'D19', 37: 'S5 D16', 36: 'D18',
    35: 'S3 D16', 34: 'D17', 33: 'S1 D16', 32: 'D16',
    31: 'S7 D12', 30: 'D15', 29: 'S9 D10', 28: 'D14',
    27: 'S7 D10', 26: 'D13', 25: 'S5 D10', 24: 'D12',
    23: 'S3 D10', 22: 'D11', 21: 'S1 D10', 20: 'D10',
    19: 'S1 D9', 18: 'D9', 17: 'S1 D8', 16: 'D8',
    15: 'S1 D7', 14: 'D7', 13: 'S1 D6', 12: 'D6',
    11: 'S1 D5', 10: 'D5', 9: 'S1 D4', 8: 'D4',
    7: 'S1 D3', 6: 'D3', 5: 'S1 D2', 4: 'D2',
    3: 'S1 D1', 2: 'D1'
};

export function checkoutFor(remaining) {
    if (CHECKOUTS[remaining]) return CHECKOUTS[remaining];
    if (remaining > 0 && remaining <= 40 && remaining % 2 === 0) return 'D' + (remaining / 2);
    return null;
}

// Every distinct value a single dart can register: a plain single (1-20),
// the outer bull (25), a double (2, 4, ..., 40) or the bullseye (50), or a
// triple (3, 6, ..., 60). checkoutFor's chart only ever records ONE
// conventional route per total (the one a real player would actually throw,
// preferring big triples first) - not necessarily the shortest one. 101, for
// instance, is charted as the 3-dart "T20 S1 D20", but T17 + Bull also
// finishes it in 2. minDartsForCheckout below needs the true shortest
// route, so it searches every legal combination directly instead of reading
// dart counts off that chart.
const ANY_DART_VALUES = (() => {
    const values = new Set([25, 50]);
    for (let n = 1; n <= 20; n++) {
        values.add(n);
        values.add(n * 2);
        values.add(n * 3);
    }
    return values;
})();

// Only a genuine double - including the bullseye, which counts as "double
// 25" - can legally end a leg. The outer bull (25) is a valid dart anywhere
// else in a checkout, but reaching exactly 0 on it is a bust, not a finish.
const CHECKOUT_DART_VALUES = [50];
for (let n = 1; n <= 20; n++) CHECKOUT_DART_VALUES.push(n * 2);

// Whether some sequence of `darts` darts, each any legal single-dart value,
// can sum to exactly `target`. Only 0, 1 or 2 darts are ever asked for here
// (the darts before the final, checkout-only double) - brute force is more
// than fast enough at that size.
function canReachWithDarts(target, darts) {
    if (darts === 0) return target === 0;
    if (darts === 1) return ANY_DART_VALUES.has(target);
    for (const first of ANY_DART_VALUES) {
        if (ANY_DART_VALUES.has(target - first)) return true;
    }
    return false;
}

// The true minimum number of darts a checkout of this remaining total could
// possibly take, computed from real dart values and the real "must finish on
// a double" rule - not read off checkoutFor's chart, which records each
// total's conventional route rather than its shortest one (see above). Used
// to gate which dart-count a player can pick after a checkout, since e.g. a
// genuine 2-dart finish can't have taken only 1 dart.
//
// A handful of totals (169, 168, 166, 165, 163, 162, 159) have no valid
// checkout at all in real darts - this app doesn't verify a turn's score is
// a legal 3-dart total before treating it as a finish, so one of those could
// theoretically still reach here. None of them are reachable in 1, 2, or 3
// darts either, so the search below naturally falls through every case and
// the final `return 3` is what applies - the same "never allow fewer than
// the real minimum" safety the old chart-based fallback aimed for.
export function minDartsForCheckout(remaining) {
    for (let darts = 1; darts <= 3; darts++) {
        for (const finishValue of CHECKOUT_DART_VALUES) {
            if (finishValue > remaining) continue;
            if (canReachWithDarts(remaining - finishValue, darts - 1)) return darts;
        }
    }
    return 3;
}

// Ranked by sets won (the match is decided by sets) - the winner is
// whoever reached the target first, everyone else by how far they got.
export function rankPlayers(players, scores) {
    return players.slice().sort((a, b) => scores[b] - scores[a]);
}

export function createX01Game({ id, name, description }) {
    return {
        id,
        name,
        description,
        minPlayers: 1,
        display: 'countdown',
        input: 'numpad',
        // Whether this game's own controller knows what to do with a
        // Computer player at all - checked by app.js's setup screen to
        // show/hide the Human/Computer toggle and to filter Computer
        // players out of the roster it hands this game (see
        // playersForActiveGame()) - only x01-game-controller.js currently
        // reads a `cpuPlayers` mount option and actually simulates one.
        supportsCpuPlayers: true,
        // Declares this game's setup-screen options (see app.js's
        // renderConfigRows/getConfigRowValues) - the same generic config-row
        // system Killer and Snooker use. `accent: 'secondary'` on Starting
        // score is the one visual wrinkle: it's the actual game variant
        // (301/501/701/1001), not a match-format detail like the other two,
        // so it keeps its own distinct color.
        configRows: [
            {
                id: 'startingScore',
                label: 'Starting score',
                accent: 'secondary',
                default: String(DEFAULT_STARTING_SCORE),
                numeric: true,
                options: STARTING_SCORE_OPTIONS.map(value => ({ value: String(value), label: String(value) }))
            },
            {
                id: 'bestOfSets',
                label: 'Sets (best of)',
                default: String(DEFAULT_BEST_OF_SETS),
                numeric: true,
                options: BEST_OF_OPTIONS.map(value => ({ value: String(value), label: String(value) }))
            },
            {
                id: 'bestOfLegs',
                label: 'Legs per set (best of)',
                default: String(DEFAULT_BEST_OF_LEGS),
                numeric: true,
                options: BEST_OF_OPTIONS.map(value => ({ value: String(value), label: String(value) }))
            }
        ],
        isBust,
        isValidTurnScore,
        checkoutFor,
        minDartsForCheckout,
        toWin,
        rankPlayers
    };
}
